import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// =====================================================================
// PRODUCTION-READY QUERY CLIENT CONFIGURATION
// =====================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache configuration
      staleTime: 2 * 60 * 1000, // 2 minutes - default for frequently changing data
      gcTime: 10 * 60 * 1000, // 10 minutes - garbage collection time
      
      // Network resilience
      retry: 3, // Retry failed requests 3 times
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      
      // Refetch behavior
      refetchOnWindowFocus: false, // Don't auto-refetch on focus (better UX, less traffic)
      refetchOnReconnect: true, // Refetch when internet reconnects
      refetchOnMount: false, // Don't refetch on component mount if data is fresh
      
      // Error handling
      throwOnError: false, // Handle errors gracefully in components
      
      // Logging (development only)
      onError: (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[React Query] Query Error:', error);
        }
      },
      onSuccess: (data) => {
        if (process.env.NODE_ENV === 'development') {
          // Optional: log successful queries in dev
          // console.log('[React Query] Query Success:', data);
        }
      },
    },
    mutations: {
      // Network resilience for mutations
      retry: 2, // Retry mutations twice on failure
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
      
      // Error handling
      onError: (error) => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[React Query] Mutation Error:', error);
        }
      },
      onSuccess: (data) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[React Query] Mutation Success:', data);
        }
      },
    },
  },
});

// =====================================================================
// GLOBAL DATA INVALIDATION HANDLER
// Integrates with existing "dados-atualizados" event system
// =====================================================================

function QueryInvalidationListener() {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    const handleDataUpdate = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[React Query] Invalidating queries due to data update event');
      }
      
      // Invalidate frequently changing data
      queryClient.invalidateQueries({ queryKey: ['intencoes'] });
      queryClient.invalidateQueries({ queryKey: ['dados'] });
      
      // Note: institutos and metodos are NOT invalidated here
      // They change rarely and have longer cache times (30 min)
    };
    
    window.addEventListener('dados-atualizados', handleDataUpdate);
    return () => window.removeEventListener('dados-atualizados', handleDataUpdate);
  }, [queryClient]);
  
  return null;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // Temporarily disabled StrictMode due to performance issues with heavy pages
  // <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <QueryInvalidationListener />
      <App />
      {/* DevTools only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      )}
    </QueryClientProvider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
